import { useEffect, useState } from 'react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { adminQueryCount } from '@/client/admin.client';
import { ADMIN_COUNT_DELAY_MS, createdSinceFilter } from '@/lib/admin-aggregations';

const BUCKET_KEYS = [0, 1, 2, 3, 4, 5, 6];

/** 로컬 자정 ms — daysAgo=0 = 오늘 자정, daysAgo=6 = 6일 전 자정(오늘 포함 7일 윈도우 시작). */
function localMidnightMs(daysAgo: number): number {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() - daysAgo);
  return d.getTime();
}

interface AttendanceDistributionData {
  weekStart: number;
  total: number;
  buckets: number[];
}

export function AttendanceDistributionPanel() {
  const [data, setData] = useState<AttendanceDistributionData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    const weekStartMs = localMidnightMs(6);

    // 기존 복합 인덱스(users: isGuest+createdAt, isGuest+attendance.checkInCount+createdAt)를
    // 그대로 활용하기 위해 isGuest==false(비게스트) 를 prefix 로 둔다. 이 필터가 없으면
    // (checkInCount,createdAt) 신규 인덱스가 필요하다. → 비게스트 가입 cohort 의 분포.
    const nonGuest = { field: 'isGuest', operator: '==' as const, value: false };

    // V8 는 함수명당 초당 10회(sliding window)만 허용 → 한 건씩 응답을 받은 뒤
    // 넉넉히 쉬어(ADMIN_COUNT_DELAY_MS) 초당 호출 수를 낮춘다(순차).
    (async () => {
      try {
        const total = await adminQueryCount({
          collection: 'users',
          filters: [nonGuest, createdSinceFilter(weekStartMs)],
        });
        await new Promise((r) => setTimeout(r, ADMIN_COUNT_DELAY_MS));
        const bucketCounts: number[] = [];
        for (const k of BUCKET_KEYS) {
          bucketCounts.push(
            await adminQueryCount({
              collection: 'users',
              filters: [
                nonGuest,
                createdSinceFilter(weekStartMs),
                { field: 'attendance.checkInCount', operator: '==', value: k },
              ],
            })
          );
          await new Promise((r) => setTimeout(r, ADMIN_COUNT_DELAY_MS));
        }
        if (alive) setData({ weekStart: weekStartMs, total, buckets: bucketCounts });
      } catch (e) {
        if (alive) setError(e instanceof Error ? e.message : String(e));
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, []);

  const weekStartLabel = data ? new Date(data.weekStart).toLocaleDateString() : null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">출석 분포 (checkInCount)</CardTitle>
        <CardDescription>
          최근 1주 가입 비게스트 cohort 의 출석 횟수 분포.{' '}
          {weekStartLabel && <span className="font-mono text-xs">기준: {weekStartLabel} 이후</span>}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {error && (
          <Alert variant="destructive">
            <AlertDescription className="whitespace-pre-wrap break-words">
              출석 분포 페치 실패: {error}
            </AlertDescription>
          </Alert>
        )}
        {loading && !error && <p className="text-sm text-muted-foreground">출석 분포 로딩 중…</p>}
        {data && (
          <div className="flex flex-col gap-3">
            <p className="text-sm text-muted-foreground">총 {data.total.toLocaleString()}명</p>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-muted-foreground">
                  <th className="py-1 pr-3 text-left font-medium">checkInCount</th>
                  {BUCKET_KEYS.map((k) => (
                    <th key={k} className="px-2 py-1 text-right font-mono font-medium">
                      {k}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="py-1 pr-3 text-left text-muted-foreground">유저 수</td>
                  {BUCKET_KEYS.map((k) => (
                    <td key={k} className="px-2 py-1 text-right font-mono tabular-nums">
                      {(data.buckets[k] ?? 0).toLocaleString()}
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
